/** The only schemes a presigned download URL may carry. `http:` is the e2e stack's MinIO. */
const DOWNLOAD_URL_PROTOCOLS: ReadonlySet<string> = new Set(['https:', 'http:']);

/**
 * Admits a deliverable `downloadUrl` only when it is an absolute http(s) URL.
 *
 * ⚠️ The value arrives from the API and becomes a download link's `href`, so a `javascript:` (or
 * `data:`, `vbscript:`…) value would execute in the app's origin on click — React 18 still renders
 * such an `href`. A rejected URL is returned as `undefined`, so every caller renders its existing
 * "no download URL" state rather than a control that silently does nothing.
 * @param {string} [url] - The presigned URL from the deliverable DTO.
 * @returns {string | undefined} The URL unchanged when safe, otherwise `undefined`.
 * @example
 * safeDownloadUrl('https://bucket.s3.amazonaws.com/k?sig=a'); // → the same string
 * safeDownloadUrl('javascript:alert(1)'); // → undefined
 */
export function safeDownloadUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    // `new URL` rather than `URL.canParse`, which older Safari/Chrome builds lack. It throws on a
    // relative or malformed value, and normalises case and leading whitespace in the scheme.
    return DOWNLOAD_URL_PROTOCOLS.has(new URL(url).protocol) ? url : undefined;
  } catch {
    return undefined;
  }
}
